// src/components/ModernCalendarView.tsx
import React, { useState, useEffect, useMemo } from "react";
import { Calendar, dateFnsLocalizer, View, Views } from "react-big-calendar";
import withDragAndDrop from "react-big-calendar/lib/addons/dragAndDrop";
import { format, parse, startOfWeek, getDay } from "date-fns";
import { es } from "date-fns/locale";
import { supabase } from "../../lib/supabase";
import { 
  FiCheck, FiCalendar
} from "react-icons/fi";
import { useTasksQuery } from "../../hooks/useTasksQuery";
import { googleCalendarService, GoogleCalendarEvent } from "../../services/googleCalendar";
import GlobalSidebar from "../layout/GlobalSidebar";
import GlobalHeader from "../layout/GlobalHeader";
import TaskDetailsSidebar from "../tasks/TaskDetailsSidebar";
import TaskEditModal from "../modals/TaskEditModal";
import type { Task, Project, Company, Priority } from "../../types/domain";
import "react-big-calendar/lib/css/react-big-calendar.css";
import "react-big-calendar/lib/addons/dragAndDrop/styles.css";
import "../ModernCalendarView.css";

const locales = {
  'es': es,
};

const localizer = dateFnsLocalizer({
  format,
  parse,
  startOfWeek,
  getDay,
  locales,
});

const DnDCalendar = withDragAndDrop(Calendar);

type CalendarEvent = {
  id: string;
  title: string;
  start: Date;
  end: Date;
  resource: Task & { isGoogleEvent?: boolean };
  allDay?: boolean;
};

type ViewType = 'month' | 'week' | 'day';

interface ModernCalendarViewProps {
  projectId?: string;
}

export default function ModernCalendarView({ projectId }: ModernCalendarViewProps) {
  // Estados para filtros
  const [selectedProjectId, setSelectedProjectId] = useState<string>(projectId || "");
  const [selectedCompanyId, setSelectedCompanyId] = useState<string>("");
  const [priorityFilter, setPriorityFilter] = useState<Priority | "">("");
  const [assigneeFilter, setAssigneeFilter] = useState<string>("");
  const [searchTerm, setSearchTerm] = useState<string>("");

  // Hook para cargar tareas
  const { tasks, loading, error, reload } = useTasksQuery({
    projectId: selectedProjectId || undefined,
    search: searchTerm.trim() || undefined,
    orderBy: "position",
    ascending: true,
    limit: 2000,
  });
  
  // Estados del calendario
  const [projects, setProjects] = useState<Project[]>([]);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [currentView, setCurrentView] = useState<ViewType>('month');
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedTask, setSelectedTask] = useState<Task | null>(null);
  
  // Estados para Google Calendar
  const [googleEvents, setGoogleEvents] = useState<GoogleCalendarEvent[]>([]);
  const [googleCalendarEnabled, setGoogleCalendarEnabled] = useState(false);
  
  // Estados de UI
  const [showTaskDetails, setShowTaskDetails] = useState(false);
  const [showCreateTaskModal, setShowCreateTaskModal] = useState(false);

  // Cargar proyectos y empresas
  useEffect(() => {
    const loadProjectsAndCompanies = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        
        if (session?.user) {
          const { data: allProjects } = await supabase
            .from("projects")
            .select("*")
            .order('name');

          if (allProjects) {
            setProjects(allProjects as Project[]);
          }

          const { data: allCompanies } = await supabase
            .from("companies")
            .select("*")
            .order('name');

          if (allCompanies) {
            setCompanies(allCompanies as Company[]);
          }
        }
      } catch (error) {
        console.error("Error cargando proyectos y empresas:", error);
      }
    };

    loadProjectsAndCompanies();
  }, []);

  // Auto-inicializar Google Calendar si está disponible
  useEffect(() => {
    const initializeGoogleCalendar = async () => {
      try {
        const success = await googleCalendarService.initializeWithSupabase(supabase);
        if (success) {
          setGoogleCalendarEnabled(true);
          await loadGoogleEvents();
        }
      } catch (error) {
        console.error('Error inicializando Google Calendar:', error);
      }
    };

    initializeGoogleCalendar();
  }, []);

  // Cargar eventos de Google Calendar
  const loadGoogleEvents = async () => {
    if (!googleCalendarEnabled) return;

    try {
      const start = new Date();
      start.setMonth(start.getMonth() - 1);
      const end = new Date();
      end.setMonth(end.getMonth() + 2);

      const events = await googleCalendarService.getEvents(
        start.toISOString(),
        end.toISOString()
      );
      
      setGoogleEvents(events);
    } catch (error) {
      console.error('Error cargando eventos de Google Calendar:', error);
    }
  };

  // Manejar drag and drop de eventos
  const handleEventDrop = async ({ event, start }: any) => {
    const task = event.resource;
    if (task.isGoogleEvent) return;

    try {
      const newDeadline = format(start, 'yyyy-MM-dd');
      
      await supabase
        .from("tasks")
        .update({ deadline: newDeadline })
        .eq("id", task.id);

      await reload();
    } catch (error) {
      console.error("Error actualizando fecha de tarea:", error);
    }
  };

  // Manejar doble click en evento
  const handleDoubleClickEvent = (event: CalendarEvent) => {
    const task = event.resource;
    if (!task.isGoogleEvent) {
      setSelectedTask(task);
      setShowTaskDetails(true);
    }
  };

  // Toggle completar tarea
  const toggleTaskCompletion = async (taskId: string, event?: React.MouseEvent) => {
    if (event) {
      event.stopPropagation();
    }

    const task = tasks?.find(t => t.id === taskId);
    if (!task) return;

    const newStatus = task.status === "done" ? "todo" : "done";
    
    try {
      await supabase
        .from("tasks")
        .update({ status: newStatus })
        .eq("id", taskId);

      await reload();
    } catch (error) {
      console.error("Error actualizando tarea:", error);
    }
  };

  // Filtrar tareas
  const filteredTasks = useMemo(() => {
    if (!tasks) return [];

    return tasks.filter(task => {
      if (priorityFilter && task.priority !== priorityFilter) return false;
      if (assigneeFilter && task.assignee_id !== assigneeFilter) return false;
      if (searchTerm && !task.title.toLowerCase().includes(searchTerm.toLowerCase())) return false;
      return true;
    });
  }, [tasks, priorityFilter, assigneeFilter, searchTerm]);

  // Limpiar filtros
  const clearFilters = () => {
    setPriorityFilter("");
    setAssigneeFilter("");
    setSearchTerm("");
  };

  // Convertir tareas a eventos del calendario
  const calendarEvents: CalendarEvent[] = useMemo(() => {
    const taskEvents = filteredTasks
      .filter(task => task.start_date || task.end_date || task.deadline)
      .map(task => {
        // Priorizar fechas de inicio/fin sobre deadline
        let start: Date;
        let end: Date;
        let allDay = true;

        if (task.start_date && task.end_date) {
          // Usar rango de fechas start_date -> end_date
          start = new Date(task.start_date);
          end = new Date(task.end_date);
          // Para rangos de fechas, el final debe ser el día siguiente para que se muestre correctamente
          end.setDate(end.getDate() + 1);
        } else if (task.start_date) {
          // Solo fecha de inicio
          start = new Date(task.start_date);
          end = new Date(task.start_date);
        } else if (task.end_date) {
          // Solo fecha de fin
          start = new Date(task.end_date);
          end = new Date(task.end_date);
        } else {
          // Fallback a deadline
          start = new Date(task.deadline!);
          end = new Date(task.deadline!);
        }

        return {
          id: task.id,
          title: task.title,
          start,
          end,
          resource: task,
          allDay,
        };
      });

    // Convertir eventos de Google Calendar
    const googleCalendarEvents = googleEvents.map(event => {
      const start = event.start.dateTime ? new Date(event.start.dateTime) : new Date(event.start.date!);
      const end = event.end.dateTime ? new Date(event.end.dateTime) : new Date(event.end.date!);
      
      return {
        id: `google-${event.id}`,
        title: `📅 ${event.summary}`,
        start,
        end,
        resource: {
          id: event.id,
          title: event.summary || 'Sin título',
          description: event.description,
          status: 'done',
          priority: null,
          deadline: null,
          isGoogleEvent: true,
        } as Task & { isGoogleEvent: boolean },
        allDay: !event.start.dateTime,
      };
    });

    return [...taskEvents, ...googleCalendarEvents];
  }, [filteredTasks, googleEvents]);

  // Componente para eventos personalizados
  const EventComponent = ({ event }: { event: CalendarEvent }) => {
    const task = event.resource;
    const isCompleted = task.status === "done";
    const isGoogleEvent = task.isGoogleEvent;
    
    const getPriorityClass = () => {
      if (isGoogleEvent) return "google-event";
      if (task.priority === "urgent") return "urgent-priority";
      if (task.priority === "high") return "high-priority";
      if (task.priority === "medium") return "medium-priority";
      return "low-priority";
    };

    // Calculate deadline bar color (same logic as TaskCard)
    const getDeadlineClass = () => {
      // Status-based colors (highest priority)
      if (task.status === "archived") return "status-archived"; // Blue
      if (task.status === "done") return "status-done"; // White/light
      
      // Deadline-based colors for active tasks
      if (task.deadline) {
        try {
          const d = new Date(task.deadline);
          if (!Number.isNaN(d.getTime())) {
            const today = new Date();
            d.setHours(0, 0, 0, 0);
            today.setHours(0, 0, 0, 0);
            const diffDays = Math.round((d.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
            
            if (diffDays < 0) return "deadline-expired"; // Purple (expired)
            if (diffDays <= 3) return "deadline-urgent"; // Red (3 days or less)
            if (diffDays <= 7) return "deadline-warning"; // Yellow (7 days or less)
            return "deadline-safe"; // Green (more than 7 days)
          }
        } catch {
          return "deadline-safe";
        }
      }
      
      // Default for tasks without deadline
      return "deadline-safe";
    };

    return (
      <div 
        className={`modern-event ${getPriorityClass()} ${getDeadlineClass()} ${isCompleted ? 'completed' : ''}`}
        onDoubleClick={() => handleDoubleClickEvent(event)}
      >
        <div className="event-content">
          <span className="event-title">{event.title}</span>
          {!isGoogleEvent && (
            <button
              className={`completion-check ${isCompleted ? 'checked' : ''}`}
              onClick={(e) => toggleTaskCompletion(task.id, e)}
              aria-label={isCompleted ? 'Marcar como pendiente' : 'Marcar como completado'}
            >
              {isCompleted && <FiCheck size={12} />}
            </button>
          )}
        </div>
      </div>
    );
  };

  const handleProjectSelect = (newProjectId: string) => {
    setSelectedProjectId(newProjectId);
    if (newProjectId) {
      const project = projects.find(p => p.id === newProjectId);
      if (project && project.company_id) {
        setSelectedCompanyId(project.company_id);
      }
    }
  };

  return (
    <>
      {/* Global Header */}
      <GlobalHeader
        selectedProjectId={selectedProjectId}
        selectedCompanyId={selectedCompanyId}
        projects={projects}
        companies={companies}
        viewType="calendar"
        currentView={currentView}
        onViewChange={setCurrentView}
        currentDate={currentDate}
        onDateChange={setCurrentDate}
        onRefreshClick={() => {
          reload();
          if (googleCalendarEnabled) {
            loadGoogleEvents();
          }
        }}
        onCreateTask={() => {
          setShowCreateTaskModal(true);
        }}
        onUserClick={() => {
          // User functionality
        }}
        rightControls={
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            {!googleCalendarEnabled ? (
              <button 
                className="header-btn secondary"
                onClick={async () => {
                  try {
                    const success = await googleCalendarService.initializeWithSupabase(supabase);
                    if (success) {
                      setGoogleCalendarEnabled(true);
                      await loadGoogleEvents();
                    }
                  } catch (error) {
                    console.error('Error conectando Google Calendar:', error);
                  }
                }}
                title="Conectar Google Calendar"
              >
                <FiCalendar size={16} />
                Conectar Google
              </button>
            ) : (
              <span style={{ color: '#4CAF50', fontSize: '12px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                <FiCheck size={14} />
                Google Calendar
              </span>
            )}
          </div>
        }
      />
      
      {/* Global Sidebar */}
      <GlobalSidebar
        selectedProjectId={selectedProjectId}
        selectedCompanyId={selectedCompanyId}
        priorityFilter={priorityFilter}
        assigneeFilter={assigneeFilter}
        searchTerm={searchTerm}
        onProjectChange={handleProjectSelect}
        onCompanyChange={setSelectedCompanyId}
        onPriorityChange={setPriorityFilter}
        onAssigneeChange={setAssigneeFilter}
        onSearchChange={setSearchTerm}
        onClearFilters={clearFilters}
        projects={projects}
        companies={companies}
        tasks={tasks}
        showFilters={true}
        showProjectStats={true}
      />

      <div className="modern-calendar-layout">
      {/* Main Content */}
      <div className="calendar-main">

        {/* Contenido del calendario */}
        <div className="calendar-content">
          {loading && (
            <div className="loading-state">
              Cargando tareas...
            </div>
          )}
          
          {error && (
            <div className="error-state">
              ⚠️ {error}
            </div>
          )}
          
          {!loading && !error && (
            <div className="calendar-wrapper">
              <DnDCalendar
                localizer={localizer}
                events={calendarEvents}
                startAccessor="start"
                endAccessor="end"
                style={{ height: 'calc(100vh - 140px)' }}
                view={currentView as View}
                date={currentDate}
                onView={(view) => setCurrentView(view as ViewType)}
                onNavigate={setCurrentDate}
                views={[Views.MONTH, Views.WEEK, Views.DAY]}
                toolbar={false}
                onEventDrop={handleEventDrop}
                onDoubleClickEvent={handleDoubleClickEvent}
                components={{
                  event: EventComponent,
                }}
                messages={{
                  next: "Siguiente",
                  previous: "Anterior",
                  today: "Hoy",
                  month: "Mes",
                  week: "Semana",
                  day: "Día",
                  agenda: "Agenda",
                  date: "Fecha",
                  time: "Hora",
                  event: "Evento",
                  noEventsInRange: "No hay eventos en este rango de fechas",
                  allDay: "Todo el día",
                }}
                culture="es"
                popup
                dragFromOutsideItem={null}
              />
            </div>
          )}
        </div>
      </div>

      {/* Sidebar de detalles de tarea */}
      <TaskDetailsSidebar
        task={selectedTask}
        labels={[]}
        allLabels={[]}
        isOpen={showTaskDetails && !!selectedTask}
        onClose={() => {
          setShowTaskDetails(false);
          setSelectedTask(null);
        }}
        onTaskUpdated={reload}
        onTaskDeleted={async () => {
          setShowTaskDetails(false);
          setSelectedTask(null);
          await reload();
        }}
      />

      {/* Modal de crear tarea */}
      <TaskEditModal
        task={null}
        isOpen={showCreateTaskModal}
        onClose={() => setShowCreateTaskModal(false)}
        onSave={async (newTask: Task) => {
          await reload();
          setShowCreateTaskModal(false);
        }}
        projects={projects}
        showStatusField={true}
        showDateField={true}
      />
      </div>
    </>
  );
}

